import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/modal";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useState, useEffect } from "react";
import { MdArrowUpward, MdArrowDownward } from "react-icons/md";
import { getProductsBySubCategory, resetProductsSubCategory } from "interfaces/products";
import DeleteModal from "components/actions/Delete";
import { useDisclosure } from "@chakra-ui/hooks";

interface SubCategory {
    id: string;
    name: string;
    categoryId: string;
    position?: number;
}

const SubCategoryManager = (props: {
    isOpen: any;
    onClose: any;
    categories: any[];
    subCategories: SubCategory[];
    onSubCategoriesChange: (subCategories: SubCategory[]) => void;
}) => {
    const { isOpen, onClose, categories, subCategories, onSubCategoriesChange } = props;

    const [selectedCategory, setSelectedCategory] = useState("");
    const [newSubCategoryName, setNewSubCategoryName] = useState("");
    const [editingSubCategory, setEditingSubCategory] = useState<SubCategory | null>(null);
    const [deleteModalData, setDeleteModalData] = useState<{subCat: SubCategory, products: any[]} | null>(null);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

    const getSubCategoryId = (name: string, categoryId: string) => {
        return `${categoryId}-${name.toLowerCase().replace(/\s+/g, "-")}`;
    };

    const addSubCategory = () => {
        if (!newSubCategoryName.trim() || !selectedCategory) return;

        const categorySubCategories = subCategories.filter(sc => sc.categoryId === selectedCategory);
        const maxPosition = categorySubCategories.length > 0 
            ? Math.max(...categorySubCategories.map(sc => sc.position || 0))
            : -1;

        const newSubCategory: SubCategory = {
            id: getSubCategoryId(newSubCategoryName, selectedCategory),
            name: newSubCategoryName.trim(),
            categoryId: selectedCategory,
            position: maxPosition + 1,
        };

        const updatedSubCategories = [...subCategories, newSubCategory];
        onSubCategoriesChange(updatedSubCategories);
        setNewSubCategoryName("");
    };

    const removeSubCategory = (id: string) => {
        const updatedSubCategories = subCategories.filter(sc => sc.id !== id);
        onSubCategoriesChange(updatedSubCategories);
    };

    const handleRemoveSubCategory = async (subCat: SubCategory) => {
        try {
            // Vérifier si des produits utilisent cette sous-catégorie
            const productsRes = await getProductsBySubCategory(subCat.name);
            
            if (productsRes && productsRes.data && productsRes.data.length > 0) {
                // Ouvrir le modal de confirmation avec les données
                setDeleteModalData({ subCat, products: productsRes.data });
                onDeleteModalOpen();
            } else {
                // Pas de produits, supprimer directement
                removeSubCategory(subCat.id);
            }
        } catch (error) {
            console.error('Erreur lors de la suppression de la sous-catégorie:', error);
            alert('Une erreur est survenue lors de la suppression de la sous-catégorie.');
        }
    };

    const confirmDelete = async () => {
        if (!deleteModalData) return;
        
        try {
            // Réinitialiser les produits
            await resetProductsSubCategory(deleteModalData.subCat.name);
            // Supprimer la sous-catégorie
            removeSubCategory(deleteModalData.subCat.id);
            onDeleteModalClose();
            setDeleteModalData(null);
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            alert('Une erreur est survenue lors de la suppression de la sous-catégorie.');
        }
    };

    const updateSubCategory = () => {
        if (!editingSubCategory || !editingSubCategory.name.trim()) return;

        const updatedSubCategories = subCategories.map(sc =>
            sc.id === editingSubCategory.id ? editingSubCategory : sc
        );
        onSubCategoriesChange(updatedSubCategories);
        setEditingSubCategory(null);
    };

    const moveSubCategory = (subCatId: string, direction: 'up' | 'down') => {
        const subCat = subCategories.find(sc => sc.id === subCatId);
        if (!subCat) return;

        const categorySubCategories = subCategories
            .filter(sc => sc.categoryId === subCat.categoryId)
            .sort((a, b) => (a.position || 0) - (b.position || 0));

        const currentIndex = categorySubCategories.findIndex(sc => sc.id === subCatId);
        if (currentIndex === -1) return;
        if (direction === 'up' && currentIndex === 0) return;
        if (direction === 'down' && currentIndex === categorySubCategories.length - 1) return;

        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        // Échanger les positions
        const updatedSubCategories = subCategories.map(sc => {
            if (sc.id === categorySubCategories[currentIndex].id) {
                return { ...sc, position: categorySubCategories[swapIndex].position };
            }
            if (sc.id === categorySubCategories[swapIndex].id) {
                return { ...sc, position: categorySubCategories[currentIndex].position };
            }
            return sc;
        });

        onSubCategoriesChange(updatedSubCategories);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[500px] !max-w-[85%] top-[8vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[750px] flex flex-col !z-[1004]">
                        <h4 className="text-lg font-bold mb-4">
                            Gérer les sous-catégories
                        </h4>

                        {/* Add new sub-category */}
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                            <h5 className="text-sm font-semibold mb-3">Ajouter une sous-catégorie</h5>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="text-xs text-navy-700 font-medium mb-1 block">
                                        Catégorie parente
                                    </label>
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">Sélectionner une catégorie</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.icon !== 'none' && `${cat.icon} `}{cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <InputField
                                        label="Nom de la sous-catégorie"
                                        placeholder="Ex: Fruits, légumes, etc..."
                                        id="subCategoryName"
                                        type="text"
                                        value={newSubCategoryName}
                                        onChange={(e: any) => setNewSubCategoryName(e.target.value)}
                                        inputSmall={true}
                                    />
                                </div>
                                <button
                                    onClick={addSubCategory}
                                    disabled={!selectedCategory || !newSubCategoryName.trim()}
                                    className="linear rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Ajouter
                                </button>
                            </div>
                        </div>

                        {/* List existing sub-categories */}
                        <div className="mb-4">
                            <h5 className="text-sm font-semibold mb-3">Sous-catégories existantes</h5>
                            <div className="max-h-[300px] overflow-y-auto">
                                {categories.map((category) => {
                                    const categorySubCategories = subCategories
                                        .filter(sc => sc.categoryId === category.id)
                                        .sort((a, b) => (a.position || 0) - (b.position || 0));
                                    
                                    if (categorySubCategories.length === 0) return null;

                                    return (
                                        <div key={category.id} className="mb-4">
                                            <h6 className="text-xs font-semibold text-slate-600 mb-2">
                                                {category.name}
                                            </h6>
                                            <div className="space-y-2">
                                                {categorySubCategories.map((subCat, index) => (
                                                    <div
                                                        key={subCat.id}
                                                        className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg"
                                                    >
                                                        {editingSubCategory?.id === subCat.id ? (
                                                            <input
                                                                type="text"
                                                                value={editingSubCategory.name}
                                                                onChange={(e) => setEditingSubCategory({
                                                                    ...editingSubCategory,
                                                                    name: e.target.value
                                                                })}
                                                                className="flex-1 px-2 py-1 border border-blue-500 rounded text-sm focus:outline-none"
                                                            />
                                                        ) : (
                                                            <span className="text-sm">{subCat.name}</span>
                                                        )}
                                                        <div className="flex gap-2">
                                                            {editingSubCategory?.id !== subCat.id && (
                                                                <>
                                                                    <button
                                                                        onClick={() => moveSubCategory(subCat.id, 'up')}
                                                                        disabled={index === 0}
                                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                                        title="Déplacer vers le haut"
                                                                    >
                                                                        <MdArrowUpward size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => moveSubCategory(subCat.id, 'down')}
                                                                        disabled={index === categorySubCategories.length - 1}
                                                                        className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
                                                                        title="Déplacer vers le bas"
                                                                    >
                                                                        <MdArrowDownward size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                            {editingSubCategory?.id === subCat.id ? (
                                                                <>
                                                                    <button
                                                                        onClick={updateSubCategory}
                                                                        className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                                                    >
                                                                        ✓
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setEditingSubCategory(null)}
                                                                        className="text-xs px-2 py-1 bg-gray-400 text-white rounded hover:bg-gray-500"
                                                                    >
                                                                        ✗
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={() => setEditingSubCategory(subCat)}
                                                                        className="text-xs px-2 py-1 bg-toola-500 text-white rounded hover:bg-blue-700"
                                                                    >
                                                                        Modifier
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleRemoveSubCategory(subCat)}
                                                                        className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                                                    >
                                                                        Supprimer
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                                {subCategories.length === 0 && (
                                    <p className="text-sm text-slate-500 text-center py-4">
                                        Aucune sous-catégorie créée
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mt-5 flex gap-2 justify-end">
                            <button
                                onClick={onClose}
                                className="linear rounded-xl bg-toola-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-toola-600"
                            >
                                Fermer
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
            
            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    onDeleteModalClose();
                    setDeleteModalData(null);
                }}
                description={
                    deleteModalData 
                        ? `${deleteModalData.products.length} produit(s) utilisent cette sous-catégorie : ${deleteModalData.products.slice(0, 5).map((p: any) => p.name).join(', ')}${deleteModalData.products.length > 5 ? ` et ${deleteModalData.products.length - 5} autre(s)...` : ''}. Si vous supprimez cette sous-catégorie, ces produits n'auront plus de sous-catégorie.`
                        : ''
                }
                context="une sous-catégorie"
                deleteFunction={confirmDelete}
            />
        </Modal>
    );
};

export default SubCategoryManager;
